/*
 * Page data capture for the local LM Studio assistant.
 *
 * Records the API responses the current page loads (GET requests made via
 * api-client) so the assistant can answer from ALL the page's data — every
 * row of a paginated table, collapsed sections, etc. — not just what's
 * visible. Capture is OFF unless the assistant is enabled for this user
 * (`local_lm_studio_user`), and nothing here leaves the browser except via
 * the assistant, which only talks to LM Studio on localhost.
 *
 * Before sending, data is compacted for the model: UUIDs become short,
 * consistent aliases (#1, #2 …) so relationships across responses survive
 * at a fraction of the tokens; nulls / empties are dropped; datetimes are
 * trimmed to dates; rich-text deltas become plain text; very long strings
 * are truncated.
 */

export interface CapturedResponse {
  /** Endpoint path, e.g. /project_read */
  url: string;
  params?: Record<string, unknown>;
  data: unknown;
  /** Page (pathname + search) the response was captured on. */
  page: string;
  at: number;
}

export interface PageContextSource {
  label: string;
  text: string;
  /** Set when the source was cut down to fit the budget. */
  truncated?: string;
}

const MAX_ENTRIES = 80;
const MAX_STRING_CHARS = 2000;

// Endpoints every page calls for app chrome (auth, permissions, badges).
// They're only treated as page data on the page that's actually about them.
const GLOBAL_ENDPOINTS: Record<string, string | null> = {
  '/check_function_permissions': null,
  '/impersonatable_users_read': null,
  '/permissions_read': '/settings',
  '/permissions_group_read': '/settings',
  '/user_read': '/settings',
  '/tasks_read': '/tasks',
  '/bookmark_read': null,
};

let enabled = false;
let entries: CapturedResponse[] = [];

const currentPage = () => `${window.location.pathname}${window.location.search}`;

export const setPageCaptureEnabled = (on: boolean) => {
  enabled = on;
  if (!on) entries = [];
};

/** Called by api-client for every successful request. */
export const capturePageData = (config: { method?: string, url?: string, params?: unknown }, data: unknown) => {
  if (!enabled) return;
  try {
    if ((config.method || 'GET').toUpperCase() !== 'GET' || !config.url) return;
    const url = config.url.split('?')[0];
    const page = currentPage();
    if (url in GLOBAL_ENDPOINTS) {
      const allowedOn = GLOBAL_ENDPOINTS[url];
      if (!allowedOn || !window.location.pathname.startsWith(allowedOn)) return;
    }
    let params: Record<string, unknown> | undefined;
    if (config.params instanceof URLSearchParams) {
      params = {};
      config.params.forEach((v, k) => {
        const existing = params![k];
        params![k] = existing === undefined ? v : ([] as unknown[]).concat(existing, v);
      });
    } else if (config.params && typeof config.params === 'object') {
      params = config.params as Record<string, unknown>;
    }
    // Same request re-fetched on the same page (polling, cache refresh) → replace.
    const key = `${page}|${url}|${JSON.stringify(params ?? {})}`;
    entries = entries.filter((e) => `${e.page}|${e.url}|${JSON.stringify(e.params ?? {})}` !== key);
    entries.push({
      url, params, data, page, at: Date.now(),
    });
    if (entries.length > MAX_ENTRIES) entries = entries.slice(-MAX_ENTRIES);
  } catch {
    // Capture must never break a request.
  }
};

/** Responses captured on the page currently shown. */
export const getCapturedForCurrentPage = (): CapturedResponse[] => {
  const page = currentPage();
  return entries.filter((e) => e.page === page);
};

// ---------------------------------------------------------------- compaction

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const ISO_DATETIME_RE = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Stable UUID → alias map, shared across all sources for one conversation. */
export class AliasMap {
  private map = new Map<string, string>();

  alias(uuid: string): string {
    const key = uuid.toLowerCase();
    let a = this.map.get(key);
    if (!a) {
      a = `#${this.map.size + 1}`;
      this.map.set(key, a);
    }
    return a;
  }

  get size() {
    return this.map.size;
  }

  /** For saving the conversation: aliases must mean the same thing after a reload. */
  toEntries(): [string, string][] {
    return Array.from(this.map.entries());
  }

  static fromEntries(saved: unknown): AliasMap {
    const m = new AliasMap();
    if (Array.isArray(saved)) {
      saved.forEach((e) => {
        if (Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'string') m.map.set(e[0], e[1]);
      });
    }
    return m;
  }
}

const deltaToText = (s: string): string | null => {
  const t = s.trimStart();
  if (!(t.startsWith('[{"insert"') || t.startsWith('{"ops"'))) return null;
  try {
    const parsed = JSON.parse(s);
    const ops = Array.isArray(parsed) ? parsed : parsed.ops;
    if (!Array.isArray(ops)) return null;
    return ops.map((op: { insert?: unknown }) => (typeof op.insert === 'string' ? op.insert : '')).join('').trim();
  } catch {
    return null;
  }
};

const compactString = (s: string, aliases: AliasMap): string => {
  const iso = ISO_DATETIME_RE.exec(s);
  if (iso) return iso[1];
  const text = deltaToText(s) ?? s;
  const aliased = text.replace(UUID_RE, (m) => aliases.alias(m));
  return aliased.length > MAX_STRING_CHARS ? `${aliased.slice(0, MAX_STRING_CHARS)}… [truncated]` : aliased;
};

const isEmpty = (v: unknown) => v === null || v === undefined || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !(v instanceof Date) && !Array.isArray(v) && Object.keys(v as object).length === 0);

type Row = Record<string, unknown>;
const isRecordList = (v: unknown): v is Row[] => Array.isArray(v) && v.length >= 2
  && v.every((x) => x && typeof x === 'object' && !Array.isArray(x));

/**
 * Lists of records become tables: the column names once, then one row per
 * record. Repeating every key on every row is most of a list's tokens, so a
 * table fits far more rows into the budget (e.g. a whole people list rather
 * than the first page of it). Applied at every depth.
 */
function tabulate(value: unknown): unknown {
  if (isRecordList(value)) {
    const columns: string[] = [];
    const seen = new Set<string>();
    value.forEach((it) => Object.keys(it).forEach((k) => {
      if (!seen.has(k)) { seen.add(k); columns.push(k); }
    }));
    return { columns, rows: value.map((it) => columns.map((c) => (c in it ? tabulate(it[c]) : null))) };
  }
  if (Array.isArray(value)) return value.map(tabulate);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Row).map(([k, v]) => [k, tabulate(v)]));
  }
  return value;
}

const toTable = (items: Row[]) => tabulate(items) as { columns: string[]; rows: unknown[][] };

/** Recursively shrink a value for the model (see module comment). */
export const compactValue = (value: unknown, aliases: AliasMap): unknown => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
  if (typeof value === 'string') return compactString(value, aliases);
  if (Array.isArray(value)) {
    return value.map((v) => compactValue(v, aliases)).filter((v) => !isEmpty(v));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (k.startsWith('_')) return; // internal bookkeeping (_version etc.)
      const c = compactValue(v, aliases);
      if (!isEmpty(c)) out[k] = c;
    });
    return out;
  }
  return value;
};

/** Rough token estimate (no tokenizer in the browser): ~3.5 chars/token for compact JSON. */
export const estimateTokens = (text: string) => Math.ceil(text.length / 3.5);

const describe = (e: CapturedResponse, aliases: AliasMap) => {
  const params = e.params && Object.keys(e.params).length
    ? ` ${JSON.stringify(compactValue(e.params, aliases))}` : '';
  return `${e.url}${params}`;
};

/**
 * Build the page-data sources to send, fitted to a character budget.
 * Arrays that don't fit are cut to the first N items with a note, so the
 * model (and you) know the answer may be based on partial data.
 */
export const buildDataSources = (captured: CapturedResponse[], aliases: AliasMap, budgetChars: number): PageContextSource[] => {
  const prepared = captured.map((entry) => {
    const compacted = compactValue(entry.data, aliases);
    const table = isRecordList(compacted) ? toTable(compacted) : null;
    const value = table ?? tabulate(compacted);
    return {
      label: describe(entry, aliases), compacted, table, value, full: JSON.stringify(value),
    };
  }).filter((p) => !isEmpty(p.compacted));
  // Smallest first: lookups (departments, skills, clients…) are cheap and are
  // what turn ids into names; the big list then gets whatever is left.
  prepared.sort((a, b) => a.full.length - b.full.length);

  const sources: PageContextSource[] = [];
  let remaining = budgetChars;
  prepared.forEach(({
    label, table, compacted, full,
  }) => {
    const tabular = table ? ` (table: ${table.rows.length} rows)` : '';
    if (full.length <= remaining) {
      sources.push({ label: `${label}${tabular}`, text: full });
      remaining -= full.length;
      return;
    }
    if (remaining < 200) {
      sources.push({ label, text: '', truncated: 'left out — page data budget used up' });
      return;
    }
    const items: unknown[] | null = table ? table.rows : (Array.isArray(compacted) ? compacted.map(tabulate) : null);
    if (items) {
      // Largest prefix of rows / items that fits.
      const render = (n: number) => JSON.stringify(table ? { columns: table.columns, rows: items.slice(0, n) } : items.slice(0, n));
      let lo = 0;
      let hi = items.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (render(mid).length <= remaining) lo = mid; else hi = mid - 1;
      }
      const text = render(lo);
      sources.push({ label: `${label}${tabular}`, text, truncated: `first ${lo} of ${items.length} ${table ? 'rows' : 'items'}` });
      remaining -= text.length;
    } else {
      const text = `${full.slice(0, remaining)}…`;
      sources.push({ label, text, truncated: `first ${Math.round((remaining / full.length) * 100)}%` });
      remaining = 0;
    }
  });
  return sources;
};

/** Visible text of the page content area (excludes nav / header chrome). */
export const visiblePageText = (maxChars: number): PageContextSource | null => {
  const el = document.querySelector('[data-page-content]') as HTMLElement | null;
  const raw = (el?.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
  if (!raw) return null;
  return raw.length > maxChars
    ? { label: 'Visible page text', text: raw.slice(0, maxChars), truncated: `first ${maxChars} characters` }
    : { label: 'Visible page text', text: raw };
};
